import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { debounce } from "lodash";
import { listAppUsers, createAppUser, deleteAppUser, manageAccess } from "api/appUsers";

import { Button } from "components/ui/Button";
import { Modal } from "components/ui/Modal";
import { ConfirmDialog } from "components/ui/ConfirmDialog";

/* === Иконки === */
const PlusIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="h-5 w-5"
    viewBox="0 0 20 20"
    fill="currentColor"
  >
    <path
      fillRule="evenodd"
      d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
      clipRule="evenodd"
    />
  </svg>
);
const SearchIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="h-5 w-5"
    viewBox="0 0 20 20"
    fill="currentColor"
  >
    <path
      fillRule="evenodd"
      d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
      clipRule="evenodd"
    />
  </svg>
);
const EditIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="h-5 w-5"
    viewBox="0 0 20 20"
    fill="currentColor"
  >
    <path d="M17.414 2.586a2 2 0 00-2.828 0L7 10.172V13h2.828l7.586-7.586a2 2 0 000-2.828z" />
    <path
      fillRule="evenodd"
      d="M2 6a2 2 0 012-2h4a1 1 0 010 2H4v10h10v-4a1 1 0 112 0v4a2 2 0 01-2 2H4a2 2 0 01-2-2V6z"
      clipRule="evenodd"
    />
  </svg>
);
const DeleteIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="h-5 w-5"
    viewBox="0 0 20 20"
    fill="currentColor"
  >
    <path
      fillRule="evenodd"
      d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm4 0a1 1 0 012 0v6a1 1 0 11-2 0V8z"
      clipRule="evenodd"
    />
  </svg>
);
const ArrowUpDownIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="h-4 w-4 ml-1.5"
    viewBox="0 0 20 20"
    fill="currentColor"
  >
    <path d="M7 3l-3 3h2v8h2V6h2L7 3zM13 17l3-3h-2V6h-2v8h-2l3 3z" />
  </svg>
);

/* ===== Страница ===== */
export default function AppUsersPage() {
  const [q, setQ] = useState("");
  const [sortBy, setSortBy] = useState("full_name");
  const [sortDir, setSortDir] = useState("asc");

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(
    () => Number(localStorage.getItem("appUsersPageSize")) || 25
  );

  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [allRowsCache, setAllRowsCache] = useState(null);

  const [openNew, setOpenNew] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [manageAccessUser, setManageAccessUser] = useState(null);
  const [deletingUser, setDeletingUser] = useState(null);
  const [confirmLoading, setConfirmLoading] = useState(false);

  const totalPages = Math.max(1, Math.ceil(count / pageSize));

  // Сохранение pageSize в localStorage
  useEffect(() => {
    try {
      localStorage.setItem("appUsersPageSize", String(pageSize));
    } catch { }
  }, [pageSize]);

  const comparator = useCallback(
    (a, b) => {
      const dir = sortDir === "asc" ? 1 : -1;
      const av = (a?.[sortBy] ?? "").toString().toLowerCase();
      const bv = (b?.[sortBy] ?? "").toString().toLowerCase();
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    },
    [sortBy, sortDir]
  );

  const fetchRows = useCallback(
    async (
      searchQuery,
      currentPage,
      currentPageSize,
      currentSortBy,
      currentSortDir
    ) => {
      setLoading(true);
      try {
        const offset = (currentPage - 1) * currentPageSize;
        const result = await listAppUsers({
          q: searchQuery,
          limit: currentPageSize,
          offset,
          sortBy: currentSortBy,
          sortDir: currentSortDir,
        });

        if (
          result &&
          typeof result === "object" &&
          Array.isArray(result.rows)
        ) {
          setRows(result.rows);
          setCount(Number(result.count ?? result.rows.length) || 0);
          setAllRowsCache(null);
        } else if (Array.isArray(result)) {
          const sorted = [...result].sort(comparator);
          setAllRowsCache(sorted);
          setCount(sorted.length);
          setRows(sorted.slice(offset, offset + currentPageSize));
        } else {
          setRows([]);
          setCount(0);
          setAllRowsCache(null);
        }
      } catch (e) {
        console.error("Failed to fetch app users:", e.message);
        setRows([]);
        setCount(0);
        setAllRowsCache(null);
      } finally {
        setLoading(false);
      }
    },
    [comparator]
  );

  const debouncedFetch = useMemo(() => debounce(fetchRows, 300), [fetchRows]);

  // Сброс страницы при изменении фильтров
  useEffect(() => {
    setPage(1);
  }, [q, sortBy, sortDir, pageSize]);

  // Основной загрузчик данных
  useEffect(() => {
    debouncedFetch(q, page, pageSize, sortBy, sortDir);
    return () => debouncedFetch.cancel?.();
  }, [q, page, pageSize, sortBy, sortDir, debouncedFetch]);

  // Обновление среза при клиентской пагинации
  useEffect(() => {
    if (!allRowsCache) return;
    const sorted = [...allRowsCache].sort(comparator);
    const offset = (page - 1) * pageSize;
    setRows(sorted.slice(offset, offset + pageSize));
    setCount(sorted.length);
  }, [page, pageSize, sortBy, sortDir, allRowsCache, comparator]);

  const refreshData = () => {
    fetchRows(q, page, pageSize, sortBy, sortDir);
  };

  const doDeleteUser = async () => {
    if (!deletingUser) return;
    setConfirmLoading(true);
    try {
      await deleteAppUser(deletingUser.id);
      setDeletingUser(null);
      if (rows.length === 1 && page > 1) {
        setPage((p) => p - 1);
      } else {
        refreshData();
      }
    } catch (e) {
      alert(e?.message || "Foydalanuvchini o'chirib bo'lmadi");
    } finally {
      setConfirmLoading(false);
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 bg-gray-50/50 dark:bg-slate-900 min-h-screen transition-colors">
      <div className="max-w-screen-xl mx-auto space-y-6">
        <header className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-gray-900 dark:text-slate-100">
              Xodimlar
            </h1>
            <p className="mt-1 text-md text-gray-600 dark:text-slate-400">
              Mahsulot berish uchun xodimlar ro'yxati.
            </p>
          </div>
          <Button
            onClick={() => setOpenNew(true)}
            className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white shadow-sm"
          >
            <PlusIcon />
            <span>Xodim qo'shish</span>
          </Button>
        </header>

        {/* НОВАЯ ПАНЕЛЬ ФИЛЬТРОВ */}
        <div className="flex flex-col md:flex-row items-center gap-4 flex-wrap">
          <div className="relative w-full md:flex-1 md:min-w-[250px]">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <span className="text-gray-400 dark:text-slate-500">
                <SearchIcon />
              </span>
            </div>
            <input
              type="text"
              placeholder="F.I.O, bo'lim bo'yicha qidirish..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-700 rounded-lg shadow-sm text-gray-900 dark:text-slate-100 placeholder-gray-400 dark:placeholder-slate-500"
            />
          </div>
          <select
            className="w-full md:w-auto bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-700 rounded-lg shadow-sm py-2 px-3 text-gray-900 dark:text-slate-100"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
          >
            <option value="full_name">Saralash: F.I.O</option>
            <option value="department">Saralash: Bo'lim</option>
            <option value="room_no">Saralash: Xona</option>
            <option value="phone">Saralash: Telefon</option>
            <option value="created_at">Saralash: Sana</option>
          </select>
          <Button
            variant="outline"
            className="bg-white dark:bg-slate-800 dark:border-slate-700 dark:text-slate-200"
            onClick={() => setSortDir((d) => (d === "asc" ? "desc" : "asc"))}
            title={sortDir === "asc" ? "O'sish bo'yicha" : "Kamayish bo'yicha"}
          >
            {sortDir === "asc" ? "O'sish bo'yicha" : "Kamayish bo'yicha"}
            <ArrowUpDownIcon />
          </Button>
          <div className="hidden md:flex flex-grow" />
          <PageSizeSelector value={pageSize} onChange={setPageSize} />
        </div>

        <div>
          {loading && rows.length === 0 ? (
            <SkeletonLayout />
          ) : rows.length === 0 ? (
            <EmptyState onCreate={() => setOpenNew(true)} />
          ) : (
            <>
              <AppUsersTable
                users={rows}
                onEdit={setEditingUser}
                onManageAccess={setManageAccessUser}
                onDelete={setDeletingUser}
              />
              {count > pageSize && (
                <Pagination
                  currentPage={page}
                  totalPages={totalPages}
                  onPageChange={setPage}
                  totalCount={count}
                  pageSize={pageSize}
                />
              )}
            </>
          )}
        </div>
      </div>

      <CreateAppUserModal
        open={openNew}
        onClose={() => setOpenNew(false)}
        onCreated={refreshData}
      />
      <EditAppUserModal
        open={!!editingUser}
        user={editingUser}
        onClose={() => setEditingUser(null)}
      />
      <ManageAccessModal
        open={!!manageAccessUser}
        user={manageAccessUser}
        onClose={() => setManageAccessUser(null)}
      />
      <ConfirmDialog
        open={!!deletingUser}
        onClose={() => setDeletingUser(null)}
        title="Foydalanuvchini o'chirish"
        message={`Haqiqatan ham "${deletingUser?.full_name}" foydalanuvchisini o'chirmoqchimisiz? Bu amalni qaytarib bo'lmaydi.`}
        onOk={doDeleteUser}
        loading={confirmLoading}
      />
    </div>
  );
}

/* ===== Таблица/карточки ===== */
const AppUsersTable = ({ users, onEdit, onDelete, onManageAccess }) => (
  <div>
    {/* Мобайл: карточки */}
    <div className="sm:hidden space-y-4">
      {users.map((user) => (
        <div
          key={user.id}
          className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200/75 dark:border-slate-700/75 p-4 space-y-3 shadow-sm"
        >
          <div className="flex justify-between items-start">
            <div className="flex items-center gap-3 min-w-0">
              <Avatar name={user.full_name} photoUrl={user.photo_url} />
              <div className="min-w-0">
                <Link
                  className="font-semibold text-gray-800 dark:text-slate-200 hover:text-blue-600 dark:hover:text-blue-400 truncate block"
                  to={`/users/${user.id}`}
                >
                  {user.full_name || "Ismsiz"}
                </Link>
                <p className="text-sm text-gray-500 dark:text-slate-400 truncate">
                  {user.email || "—"}
                </p>
              </div>
            </div>
            <div className="flex-shrink-0">
              <ActionsMenu user={user} onEdit={onEdit} onDelete={onDelete} onManageAccess={onManageAccess} />
            </div>
          </div>
          <div className="border-t border-gray-100 dark:border-slate-700 pt-3 mt-1 space-y-2 text-sm">
            <Row label="Bo'lim" value={user.department} />
            <Row label="Xona" value={user.room_no} />
            <Row label="Telefon" value={user.phone} mono />
          </div>
        </div>
      ))}
    </div>

    {/* Десктоп: таблица */}
    <div className="hidden sm:block overflow-hidden bg-white dark:bg-slate-800 rounded-lg border border-gray-200/75 dark:border-slate-700/75">
      <table className="w-full">
        <thead className="bg-gray-50/75 dark:bg-slate-700/50 border-b border-gray-200/75 dark:border-slate-700/75">
          <tr className="text-left text-xs uppercase text-gray-500 dark:text-slate-400 tracking-wider">
            <th className="py-3 px-6 font-semibold text-sm w-2/5">
              Xodim
            </th>
            <th className="py-3 px-6 font-semibold text-sm">Bo'lim</th>
            <th className="py-3 px-6 font-semibold text-sm">Xona</th>
            <th className="py-3 px-6 font-semibold text-sm">Telefon</th>
            <th className="py-3 px-6 font-semibold text-sm w-16 text-center">
              Amallar
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 dark:divide-slate-700">
          {users.map((user) => (
            <tr
              key={user.id}
              className="bg-white dark:bg-slate-800 hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors group"
            >
              <td className="py-4 px-6">
                <div className="flex items-center gap-3">
                  <Avatar name={user.full_name} photoUrl={user.photo_url} />
                  <div>
                    <Link
                      className="font-semibold text-gray-800 dark:text-slate-200 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                      to={`/users/${user.id}`}
                    >
                      {user.full_name || "Ismsiz"}
                    </Link>
                    <p className="text-sm text-gray-500 dark:text-slate-400">{user.email || "—"}</p>
                  </div>
                </div>
              </td>
              <td className="py-4 px-6 text-gray-600 dark:text-slate-400">
                {user.department || "—"}
              </td>
              <td className="py-4 px-6 text-gray-600 dark:text-slate-400">{user.room_no || "—"}</td>
              <td className="py-4 px-6 text-gray-600 dark:text-slate-400 font-mono">
                {user.phone || "—"}
              </td>
              <td className="py-4 px-6 text-center">
                <ActionsMenu user={user} onEdit={onEdit} onDelete={onDelete} onManageAccess={onManageAccess} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const Row = ({ label, value, mono = false }) => (
  <div className="flex justify-between items-center">
    <span className="font-medium text-gray-500 dark:text-slate-400">{label}:</span>
    <span className={`text-gray-700 dark:text-slate-300 ${mono ? "font-mono" : ""}`}>
      {value || "—"}
    </span>
  </div>
);

/* ===== Модалки ===== */
const CreateAppUserModal = ({ open, onClose, onCreated }) => {
  const [form, setForm] = useState({
    full_name: "",
    department: "",
    room_no: "",
    phone: "",
    email: "",
    password: "",
    photo: null,
  });
  const [photoPreview, setPhotoPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setForm({ full_name: "", department: "", room_no: "", phone: "", email: "", photo: null });
      setPhotoPreview(null);
      setErr(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  }, [open]);

  const handlePhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      // Проверка типа файла
      if (!file.type.startsWith('image/')) {
        setErr("Iltimos, rasm tanlang");
        return;
      }
      // Проверка размера (макс 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setErr("Rasm hajmi 5MB dan oshmasligi kerak");
        return;
      }
      setForm((prev) => ({ ...prev, photo: file }));
      // Создаем превью
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
      setErr(null);
    }
  };

  const saveUser = async () => {
    if (!form.full_name) {
      setErr("F.I.O majburiy maydon.");
      return;
    }
    setSaving(true);
    setErr(null);
    try {
      await createAppUser(form);
      onCreated?.();
      onClose();
    } catch (e) {
      setErr(e.message);
    } finally {
      setSaving(false);
    }
  };

  const inputStyles =
    "w-full bg-white dark:bg-slate-800 border-gray-300 dark:border-slate-700 text-gray-900 dark:text-slate-100 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 transition";
  const handleFieldChange = (field, value) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Yangi xodim"
      className="bg-gray-50 text-gray-800 dark:text-slate-100"
      footer={
        <div className="flex items-center justify-end w-full gap-3">
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Bekor qilish
          </Button>
          <Button
            onClick={saveUser}
            disabled={saving}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            {saving ? "Saqlanmoqda..." : "Saqlash"}
          </Button>
        </div>
      }
    >
      <div className="p-1 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
              F.I.O <span className="text-red-500">*</span>
            </label>
            <input
              value={form.full_name}
              onChange={(e) => handleFieldChange("full_name", e.target.value)}
              className={inputStyles}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
              Bo'lim
            </label>
            <input
              value={form.department}
              onChange={(e) => handleFieldChange("department", e.target.value)}
              className={inputStyles}
            />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
              Xona
            </label>
            <input
              value={form.room_no}
              onChange={(e) => handleFieldChange("room_no", e.target.value)}
              className={inputStyles}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
              Telefon
            </label>
            <input
              value={form.phone}
              onChange={(e) => handleFieldChange("phone", e.target.value)}
              className={inputStyles}
            />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
            Email
          </label>
          <input
            type="email"
            value={form.email}
            onChange={(e) => handleFieldChange("email", e.target.value)}
            className={inputStyles}
          />
        </div>

        {/* Поле загрузки фото */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
            Rasm
          </label>
          <div className="space-y-2">
            {photoPreview && (
              <div className="relative w-32 h-32 rounded-lg overflow-hidden border border-gray-300 dark:border-slate-700">
                <img
                  src={photoPreview}
                  alt="Preview"
                  className="w-full h-full object-cover"
                />
                <button
                  type="button"
                  onClick={() => {
                    setForm((prev) => ({ ...prev, photo: null }));
                    setPhotoPreview(null);
                    if (fileInputRef.current) {
                      fileInputRef.current.value = "";
                    }
                  }}
                  className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                  title="Rasmni o'chirish"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handlePhotoChange}
              className="w-full text-sm text-gray-500 dark:text-slate-400 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 dark:file:bg-slate-700 dark:file:text-blue-400 dark:hover:file:bg-slate-600"
            />
            <p className="text-xs text-gray-500 dark:text-slate-500">Maksimal hajm: 5MB</p>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
            Kirish paroli (ixtiyoriy)
          </label>
          <input
            type="password"
            value={form.password}
            onChange={(e) => handleFieldChange("password", e.target.value)}
            className={inputStyles}
            placeholder="Kirish kerak bo'lmasa, bo'sh qoldiring"
          />
        </div>

        {err && <p className="text-sm text-red-600 text-center pt-2">{err}</p>}
      </div>
    </Modal>
  );
};

const EditAppUserModal = ({ open, user, onClose }) => {
  if (!open) return null;
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Tahrirlash: ${user?.full_name}`}
    >
      <p>Tahrirlash shakli...</p>
    </Modal>
  );
};
const ManageAccessModal = ({ open, user, onClose }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (open && user) {
      setEmail(user.email || "");
      setPassword("");
      setErr(null);
    }
  }, [open, user]);

  const handleSave = async () => {
    if (!email || !password) {
      setErr("Email va parol kiritilishi shart");
      return;
    }
    setSaving(true);
    try {
      await manageAccess(user.id, { email, password });
      alert("Kirish ma'lumotlari yangilandi");
      onClose();
    } catch (e) {
      setErr(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Kirishni boshqarish"
      className="bg-white dark:bg-slate-800 text-gray-900 dark:text-slate-100"
      footer={
        <div className="flex justify-end gap-3">
          <Button variant="ghost" onClick={onClose}>Bekor qilish</Button>
          <Button disabled={saving} onClick={handleSave} className="bg-blue-600 text-white">
            {saving ? "Saqlanmoqda..." : "Saqlash"}
          </Button>
        </div>
      }
    >
      <div className="space-y-4 p-1">
        <p className="text-sm text-gray-500 dark:text-slate-400">
          Ushbu xodim uchun tizimga kirish ruxsatini yoqing yoki parolni o'zgartiring.
        </p>
        <div>
          <label className="block text-sm font-medium mb-1">Email (Login)</label>
          <input
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            className="w-full bg-white dark:bg-slate-900 border border-gray-300 dark:border-slate-700 rounded-md p-2"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Yangi parol</label>
          <input
            type="password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            className="w-full bg-white dark:bg-slate-900 border border-gray-300 dark:border-slate-700 rounded-md p-2"
            placeholder="Yangi parol..."
          />
        </div>
        {err && <p className="text-red-500 text-sm">{err}</p>}
      </div>
    </Modal>
  );
};


/* ===== Меню действий ===== */
const ActionsMenu = ({ user, onEdit, onDelete, onManageAccess }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  const handleToggle = () => {
    if (!isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const menuWidth = 192;
      let left = rect.left + window.scrollX - (menuWidth - rect.width);
      if (left < 8) left = 8;
      if (left + menuWidth > window.innerWidth - 8)
        left = window.innerWidth - menuWidth - 8;
      setPosition({ top: rect.bottom + window.scrollY + 8, left });
    }
    setIsOpen(!isOpen);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleActionClick = (action) => {
    action(user);
    setIsOpen(false);
  };

  return (
    <>
      <button
        ref={triggerRef}
        onClick={handleToggle}
        className="p-2 rounded-full text-gray-500 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
        aria-label="Amallar"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-5 w-5"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
        </svg>
      </button>
      {isOpen &&
        createPortal(
          <div
            ref={menuRef}
            style={{ top: `${position.top}px`, left: `${position.left}px` }}
            className="fixed w-48 bg-white dark:bg-slate-800 rounded-lg shadow-xl ring-1 ring-black/5 dark:ring-slate-700 z-50 p-2 border dark:border-slate-700"
          >
            <MenuItem
              onClick={() => handleActionClick(onEdit)}
              icon={<EditIcon />}
            >
              Tahrirlash
            </MenuItem>
            <MenuItem
              onClick={() => handleActionClick(onManageAccess)}
              icon={
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M18 8a6 6 0 01-7.743 5.743L10 14l-1 1-1 1H6v2H2v-4l4.257-4.257A6 6 0 1118 8zm-6-4a1 1 0 100 2 2 2 0 000-2z" clipRule="evenodd" />
                </svg>
              }
            >
              Kirish so'zlamalari
            </MenuItem>
            <div className="my-1 border-t border-gray-100" />
            <MenuItem
              onClick={() => handleActionClick(onDelete)}
              icon={<DeleteIcon />}
              isDestructive
            >
              O'chirish
            </MenuItem>
          </div>,
          document.body
        )}
    </>
  );
};

const MenuItem = ({ children, icon, onClick, isDestructive = false }) => (
  <button
    onClick={onClick}
    className={`w-full text-left flex items-center gap-3 px-3 py-2 text-sm rounded-md transition-colors ${isDestructive
      ? "text-red-600 hover:bg-red-50 dark:hover:bg-slate-700"
      : "text-gray-700 dark:text-slate-200 hover:bg-gray-100 dark:hover:bg-slate-700"
      }`}
  >
    <span className="opacity-70">{icon}</span>
    <span>{children}</span>
  </button>
);

/* ===== Вспомогалки ===== */
const Avatar = ({ name, photoUrl }) => {
  const initials =
    name
      ?.split(" ")
      .map((n) => n[0])
      .slice(0, 2)
      .join("")
      .toUpperCase() || "?";
  return (
    <div className="flex-shrink-0 h-10 w-10 flex items-center justify-center bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-300 font-bold rounded-full text-md overflow-hidden">
      {photoUrl ? (
        <img
          src={photoUrl}
          alt={name || "User"}
          className="w-full h-full object-cover"
          onError={(e) => {
            // Если фото не загрузилось, показываем инициалы
            e.target.style.display = "none";
            e.target.parentElement.innerHTML = `<span>${initials}</span>`;
          }}
        />
      ) : (
        <span>{initials}</span>
      )}
    </div>
  );
};

const EmptyState = ({ onCreate }) => (
  <div className="py-20 text-center bg-white dark:bg-slate-800 rounded-lg border-2 border-dashed border-gray-300 dark:border-slate-700 transition-colors hover:border-blue-500">
    <svg
      className="mx-auto h-12 w-12 text-gray-400"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.5"
        d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M15 21v-1a6 6 0 00-1.78-4.125a4 4 0 00-6.44-6.44"
      />
    </svg>
    <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-slate-100">
      Foydalanuvchilar topilmadi
    </h3>
    <p className="mt-1 text-sm text-gray-500">
      Начните с добавления первого пользователя в систему.
    </p>
    <div className="mt-6">
      <Button
        onClick={onCreate}
        className="bg-blue-600 hover:bg-blue-700 text-white shadow-sm"
      >
        <PlusIcon /> Добавить
      </Button>
    </div>
  </div>
);

const SkeletonLayout = () => (
  <div className="animate-pulse">
    <div className="sm:hidden space-y-4">
      {[...Array(3)].map((_, i) => (
        <div
          key={i}
          className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200/75 dark:border-slate-700 p-4 space-y-4"
        >
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3 w-full">
              <div className="h-10 w-10 rounded-full bg-gray-200 dark:bg-slate-700"></div>
              <div className="space-y-2 w-3/4">
                <div className="h-4 bg-gray-200 dark:bg-slate-700 rounded w-5/6"></div>
                <div className="h-3 bg-gray-200 dark:bg-slate-700 rounded w-1/2"></div>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-100 dark:border-slate-700 pt-4 space-y-3">
            <div className="h-4 bg-gray-200 dark:bg-slate-700 rounded w-full"></div>
            <div className="h-4 bg-gray-200 dark:bg-slate-700 rounded w-4/5"></div>
          </div>
        </div>
      ))}
    </div>
    <div className="hidden sm:block bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700">
      <div className="h-12 bg-gray-50/75 dark:bg-slate-700/50 border-b border-gray-200/75 dark:border-slate-700/75" />
      <div className="divide-y divide-gray-100 dark:divide-slate-700">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="flex items-center space-x-6 h-20 px-6">
            <div className="w-2/5 flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-gray-200 dark:bg-slate-700" />
              <div className="w-full space-y-2">
                <div className="h-4 bg-gray-200 dark:bg-slate-700 rounded w-3/5" />
                <div className="h-3 bg-gray-200 dark:bg-slate-700 rounded w-2/5" />
              </div>
            </div>
            <div className="w-1/5 h-4 bg-gray-200 dark:bg-slate-700 rounded" />
            <div className="w-1/5 h-4 bg-gray-200 dark:bg-slate-700 rounded" />
            <div className="w-1/5 h-4 bg-gray-200 dark:bg-slate-700 rounded" />
            <div className="w-16 flex justify-end">
              <div className="h-8 w-8 bg-gray-200 dark:bg-slate-700 rounded-full" />
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

// НОВЫЙ КОМПОНЕНТ
function PageSizeSelector({ value, onChange }) {
  const options = [10, 25, 50, 100];
  return (
    <div className="flex items-center gap-2">
      <span className="text-sm text-gray-600 dark:text-slate-400 hidden lg:block">
        Показывать по:
      </span>
      <select
        className="w-full md:w-auto bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-700 rounded-lg shadow-sm py-2 px-3 text-sm text-gray-900 dark:text-slate-100"
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
      >
        {options.map((size) => (
          <option key={size} value={size}>
            {size}
          </option>
        ))}
      </select>
    </div>
  );
}

// НОВЫЙ КОМПОНЕНТ
const Pagination = ({
  currentPage,
  totalPages,
  onPageChange,
  totalCount,
  pageSize,
}) => {
  const from = (currentPage - 1) * pageSize + 1;
  const to = Math.min(currentPage * pageSize, totalCount);
  return (
    <div className="mt-8 flex flex-col sm:flex-row items-center justify-between text-sm gap-4">
      <p className="text-gray-600 dark:text-slate-400 text-center sm:text-left">
        Показано <span className="font-medium text-gray-900 dark:text-slate-200">{from}</span>-
        <span className="font-medium text-gray-900 dark:text-slate-200">{to}</span> из{" "}
        <span className="font-medium text-gray-900 dark:text-slate-200">{totalCount}</span>
      </p>
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          onClick={() => onPageChange(Math.max(1, currentPage - 1))}
          disabled={currentPage <= 1}
          className="bg-white dark:bg-slate-800 dark:text-slate-100 dark:border-slate-700 disabled:text-gray-300 dark:disabled:text-slate-600"
          title="Назад"
        >
          <svg
            className="h-5 w-5"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
              clipRule="evenodd"
            />
          </svg>
        </Button>
        <span className="text-gray-600 dark:text-slate-400 px-2">
          {currentPage} / {totalPages}
        </span>
        <Button
          variant="outline"
          onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
          disabled={currentPage >= totalPages}
          className="bg-white dark:bg-slate-800 dark:text-slate-100 dark:border-slate-700 disabled:text-gray-300 dark:disabled:text-slate-600"
          title="Вперёд"
        >
          <svg
            className="h-5 w-5"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
              clipRule="evenodd"
            />
          </svg>
        </Button>
      </div>
    </div>
  );
};
